import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const FuecModal = ({ item }) => {
  // Obtener el ID de la URL usando useParams
  const { id } = useParams();
  console.log("🆔 ID obtenido de la URL:", id);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [fuecData, setFuecData] = useState(item || {});
  const [loading, setLoading] = useState(!item);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Función para consultar el API del FUEC
  const consultarFuec = async (fuecId) => {
    try {
      setLoading(true);
      setError(null);
      console.log("🔍 Consultando FUEC ID:", fuecId);
      
      const response = await axios.get(`https://app.transorientesas.com/api/consultar_fuec/${fuecId}`);
      
      console.log("📦 Respuesta del API:", response.data);
      
      if (response.data.Estado && response.data.result) {
        setFuecData(response.data.result);
        console.log("✅ FUEC obtenido exitosamente:", response.data.result);
      } else {
        throw new Error(response.data.mensaje || 'FUEC no encontrado');
      }
    } catch (err) {
      console.error('❌ Error consultando FUEC:', err);
      setError(err.message);
      setFuecData({});
    } finally {
      setLoading(false);
    }
  };

  // Función para descargar PDF desde el backend
  const handleDownloadPDF = async () => {
    try {
      setDownloadingPDF(true);
      
      console.log("📄 Solicitando PDF para FUEC ID:", id);
      
      const response = await axios.get(
        `https://app.transorientesas.com/api/descargar_fuec_pdf/${id}`,
        {
          responseType: 'blob', // Importante para archivos binarios
          headers: {
            'Accept': 'application/pdf'
          }
        }
      );
      
      console.log("✅ PDF recibido, tamaño:", response.data.size);
      
      // Crear URL del blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Generar nombre del archivo
      const fileName = `FUEC_${fuecData.numero || id}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Liberar URL
      window.URL.revokeObjectURL(url);
      
      setDownloadingPDF(false);
      
    } catch (error) {
      console.error('❌ Error descargando PDF:', error);
      setDownloadingPDF(false);
      
      // Verificar tipo de error
      if (error.response) {
        if (error.response.status === 404) {
          alert('El endpoint de descarga de PDF no está disponible. Usando impresión nativa.');
        } else if (error.response.status === 500) {
          alert('Error del servidor al generar PDF. Usando impresión nativa.');
        }
      } else {
        alert('Error de conexión al descargar PDF. Usando impresión nativa.');
      }
      
      // Fallback a la versión de impresión
      handlePrint();
    }
  };

  // Efecto para cargar datos cuando cambia el ID de la URL
  useEffect(() => {
    console.log("🔄 useEffect ejecutándose, ID:", id);
    
    if (id && !item) {
      console.log("🚀 Iniciando consulta con ID:", id);
      consultarFuec(id);
    } else if (item) {
      console.log("📋 Usando datos proporcionados via props");
      setFuecData(item);
      setLoading(false);
    } else {
      console.log("⚠️ No hay ID ni item proporcionado");
      setError('No se proporcionó ID de FUEC');
      setLoading(false);
    }
  }, [id, item]);

  // Función para generar código QR
  const generateQRCode = (fuecNumber) => {
    try {
      if (!fuecNumber) {
        console.log("⚠️ No hay número de FUEC para generar QR");
        return '';
      }
      
      // URL que quieres codificar en el QR
      const qrData = `https://control.transorientesas.com/pages/comingsoon/Consultar_qr/${fuecNumber}`;
      
      // Codificar la URL para la API de QR
      const encodedData = encodeURIComponent(qrData);
      
      // Generar URL del código QR
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedData}`;
      
      console.log("📱 QR generado para:", qrData);
      
      return qrUrl;
    } catch (error) {
      console.error('❌ Error generando QR:', error);
      return '';
    }
  };

  // Efecto para generar QR cuando cambian los datos del FUEC
  useEffect(() => {
    if (fuecData && fuecData.id) {
      console.log("🎨 Generando QR para FUEC ID:", fuecData.id);
      
      const qrUrl = generateQRCode(fuecData.id);
      setQrCodeUrl(qrUrl);
    } else {
      console.log("⚠️ No hay datos de FUEC para generar QR");
      setQrCodeUrl('');
    }
  }, [fuecData]);

  // Función para formatear fechas
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('es-ES');
    } catch {
      return '';
    }
  };

  // Función para extraer día, mes y año de una fecha
    const extractDateParts = (dateString) => {
    if (!dateString) return { day: '', month: '', year: '' };
    
    try {
        // Para formato DD/MM/YYYY
        const parts = dateString.split('/');
        if (parts.length === 3) {
            return {
                day: parts[0].trim(),
                month: parts[1].trim(),
                year: parts[2].trim().split(' ')[0] // Por si hay hora después
            };
        }
        
        // Si no es DD/MM/YYYY, intentamos con el método original
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return { day: '', month: '', year: '' };
        
        // O usar el método local para evitar problemas de zona horaria
        return {
            day: date.getUTCDate().toString(),
            month: (date.getUTCMonth() + 1).toString(),
            year: date.getUTCFullYear().toString()
        };
    } catch {
        return { day: '', month: '', year: '' };
    }
};

  // Procesar datos del conductor
   const procesarConductor = (conductorString) => {
  if (!conductorString) return { nombre: '', cedula: '', licencia: '', vigencia: '' };
  
  const parts = conductorString.split(' ');
  if (parts.length < 4) return { nombre: conductorString, cedula: '', licencia: '', vigencia: '' };
  
  // Asumimos que el primer elemento es la cédula, luego nombre completo, y el último es la vigencia
  const cedula = parts[0];
  const vigenciaRaw = parts[parts.length - 1]; // Esto viene como "2028-06-04"
  const nombre = parts.slice(1, parts.length - 1).join(' ');
  
  // Formatear la fecha de vigencia correctamente SIN usar new Date()
  let vigenciaFormateada = '';
  if (vigenciaRaw && vigenciaRaw.includes('-')) {
    // Si viene en formato YYYY-MM-DD, convertir a DD/MM/YYYY
    const [year, month, day] = vigenciaRaw.split('-');
    if (year && month && day) {
      vigenciaFormateada = `${day}/${month}/${year}`;
    }
  } else {
    // Si no, intentar con el método anterior como fallback
    try {
      const date = new Date(vigenciaRaw);
      if (!isNaN(date.getTime())) {
        // Usar UTC para evitar problemas de zona horaria
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        vigenciaFormateada = `${day}/${month}/${year}`;
      }
    } catch {
      vigenciaFormateada = vigenciaRaw;
    }
  }
  
  return {
    nombre: nombre || '',
    cedula: cedula || '',
    licencia: cedula || '', // Asumiendo que la licencia es igual a la cédula
    vigencia: vigenciaFormateada
  };
};

  // Procesar datos del contrato cliente
  const procesarCliente = (contratoString) => {
    if (!contratoString) return { nit: '', cc: '', nombre: ''};
    
    const parts = contratoString.split('-');
    return {
      nit: parts[0] || '',
      cc: parts[1] || '',
      nombre: parts[2] || ''
    };
  };

  const procesarRecorrido = (contratoString) => {
    if (!contratoString) return { codigo: '', origen: '', destino: ''};
    
    const parts = contratoString.split('|');
    return {
      codigo: parts[0] || '',
      origen: parts[1] || '',
      destino: parts[2] || ''
    };
  };

  const procesarVehiculo = (contratoString) => {
    if (!contratoString) return { placa: '', empresa_afiliada: '', modelo: '', marca: '', clase: '', numero_interno: '', especificaciones: ''};
    
    const parts = contratoString.split('|');
    return {
      placa: parts[0] || '',
      empresa_afiliada: parts[1] || '',
      modelo: parts[2] || '',
      marca: parts[3] || '',
      clase: parts[4] || '',
      numero_interno: parts[5] || '',
      especificaciones: parts[6] || ''
    };
  };

  const procesarContratoCliente = (contratoString) => {
    if (!contratoString) return { numero: '', nombre_responsable: '', cedula_responsable: '', telefono_responsable: '', direccion_responsable: '' , descripcion: ''};
    
    const parts = contratoString.split('|');
    return {
      numero: parts[0] || '',
      nombre_responsable: parts[1] || '',
      cedula_responsable: parts[2] || '',
      telefono_responsable: parts[3] || '',
      direccion_responsable: parts[4] || '',
      descripcion: parts[5] || ''
    };
  };

  // Procesar datos con la información del API
  const datosCliente = procesarCliente(fuecData.contrato_cli);
  const datosContrato = procesarContratoCliente(fuecData.numero_contrato);
  const datosConductor1 = procesarConductor(fuecData.conductor1);
  const datosConductor2 = procesarConductor(fuecData.conductor2);
  const datosConductor3 = procesarConductor(fuecData.conductor3);
  const fechaInicioParts = extractDateParts(fuecData.fech_inicio);
  const fechaFinParts = extractDateParts(fuecData.fech_fin);
  const datosRecorrido = procesarRecorrido(fuecData.codigo_ciudad);
  const datosVehiculo = procesarVehiculo(fuecData.vehiculo);

  const handlePrint = () => {
    const printContent = document.getElementById('fuec-print-content');
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Formato FUEC - ${fuecData.numero}</title>
          <style>
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 15px;
              font-size: 10px;
              line-height: 1.2;
              color: #000;
            }
            .fuec-container {
              width: 100%;
              max-width: 100%;
              margin: 0 auto;
              border: 1px solid #ccc;
              padding: 15px;
              box-sizing: border-box;
            }
            .fuec-header {
              text-align: center;
              margin-bottom: 15px;
              border-bottom: 2px solid #2c3e50;
              padding-bottom: 2px;
              position: relative;
            }
            .fuec-title {
              font-size: 11px;
              color: #2c3e50;
              margin-bottom: 6px;
              font-weight: bold;
            }
            .fuec-subtitle {
              font-size: 10px;
              color: #34495e;
              font-weight: normal;
            }
            .qr-code {
              position: absolute;
              right: 0;
              top: 0;
              width: 80px;
              height: 80px;
              border: 1px solid #ccc;
            }
            .fuec-section {
              margin-bottom: 12px;
              padding-bottom: 2px;
              border-bottom: 1px solid #e0e0e0;
              page-break-inside: avoid;
            }
            .section-title {
              font-size: 10px;
              color: #2c3e50;
              margin-bottom: 6px;
              border-bottom: 1px solid #bdc3c7;
              padding-bottom: 2px;
              font-weight: bold;
            }
            .fuec-field {
              display: flex;
              flex-wrap: wrap;
              margin-bottom: 4px;
              align-items: center;
            }
            .field-label {
              font-weight: bold;
              margin-right: 5px;
              min-width: 120px;
              font-size: 10px;
            }
            .field-value {
              flex: 1;
              font-size: 10px;
              min-width: 150px;
            }
            .date-container {
              display: flex;
              gap: 20px;
            }
            .date-field {
              display: flex;
              flex-direction: column;
            }
            .date-values {
              display: flex;
              gap: 6px;
              margin-top: 2px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 6px;
              font-size: 8px;
              page-break-inside: avoid;
            }
            th, td {
              border: 1px solid #000;
              padding: 4px;
              text-align: left;
              font-size: 8px;
              word-wrap: break-word;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
              padding: 4px;
            }
            .vehicle-details {
              display: flex;
              gap: 15px;
              margin-top: 6px;
              flex-wrap: wrap;
            }
            .detail-field {
              display: flex;
              flex-direction: column;
              min-width: 100px;
            }
            .fuec-footer {
              text-align: center;
              margin-top: 15px;
              padding-top: 8px;
              border-top: 2px solid #2c3e50;
              page-break-inside: avoid;
            }
            .footer-text {
              font-weight: bold;
              margin-bottom: 6px;
              font-size: 10px;
            }
            .footer-date {
              font-style: italic;
              font-size: 8px;
            }
            .compact-row {
              margin-bottom: 2px;
            }
            .two-columns {
              display: flex;
              gap: 10px;
              flex-wrap: wrap;
            }
            .column {
              flex: 1;
              min-width: 200px;
            }
            .small-text {
              font-size: 8px;
            }
            .signature-section {
              margin-top: 10px;
              text-align: center;
            }
            .company-info {
              text-align: center;
              font-size: 8px;
              margin-top: 5px;
              line-height: 1.1;
            }
            @media print {
              body { 
                margin: 0; 
                padding: 10px;
                font-size: 10px;
              }
              .fuec-container {
                border: none;
                padding: 10px;
                margin: 0;
              }
              .no-break {
                page-break-inside: avoid;
              }
            }
            @media screen and (max-width: 768px) {
              .fuec-field {
                flex-direction: column;
                align-items: flex-start;
              }
              .field-label {
                min-width: auto;
                margin-bottom: 2px;
              }
              .field-value {
                min-width: auto;
                width: 100%;
              }
              table {
                font-size: 7px;
              }
              th, td {
                padding: 3px;
                font-size: 7px;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Estilos responsive
  const styles = {
    app: {
      backgroundColor: '#f5f5f5',
      padding: '20px',
      minHeight: '100vh',
      '@media (max-width: 768px)': {
        padding: '10px'
      }
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#f8f9fa',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        gap: '10px',
        padding: '10px'
      }
    },
    modalTitle: {
      color: '#2c3e50',
      fontSize: '1.5rem',
      margin: 0,
      '@media (max-width: 768px)': {
        fontSize: '1.2rem',
        textAlign: 'center'
      }
    },
    modalActions: {
      display: 'flex',
      gap: '10px',
      '@media (max-width: 768px)': {
        width: '100%',
        justifyContent: 'center'
      }
    },
    btnPrint: {
      backgroundColor: '#27ae60',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      '@media (max-width: 768px)': {
        padding: '10px 20px',
        fontSize: '16px',
        flex: 1
      }
    },
    btnClose: {
      backgroundColor: '#e74c3c',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      '@media (max-width: 768px)': {
        padding: '10px 20px',
        fontSize: '16px',
        flex: 1
      }
    },
    fuecContainer: {
      padding: '15px',
      fontSize: '9px',
      lineHeight: '1.2',
      backgroundColor: 'white',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      maxWidth: '210mm',
      margin: '0 auto',
      '@media (max-width: 768px)': {
        padding: '10px',
        fontSize: '8px'
      }
    },
    fuecHeader: {
      textAlign: 'center',
      marginBottom: '15px',
      borderBottom: '2px solid #2c3e50',
      paddingBottom: '2px',
      position: 'relative',
      '@media (max-width: 768px)': {
        marginBottom: '10px'
      }
    },
    fuecTitle: {
      fontSize: '11px',
      color: '#2c3e50',
      marginBottom: '6px',
      fontWeight: 'bold',
      '@media (max-width: 768px)': {
        fontSize: '10px'
      }
    },
    fuecSubtitle: {
      fontSize: '10px',
      color: '#34495e',
      fontWeight: 'normal',
      '@media (max-width: 768px)': {
        fontSize: '9px'
      }
    },
    qrCode: {
      position: 'absolute',
      right: '0',
      top: '0',
      width: '80px',
      height: '80px',
      border: '1px solid #ccc',
      padding: '2px',
      backgroundColor: 'white',
      '@media (max-width: 768px)': {
        position: 'relative',
        margin: '10px auto',
        display: 'block'
      }
    },
    fuecSection: {
      marginBottom: '12px',
      paddingBottom: '2px',
      borderBottom: '1px solid #e0e0e0',
      '@media (max-width: 768px)': {
        marginBottom: '8px'
      }
    },
    sectionTitle: {
      fontSize: '10px',
      color: '#2c3e50',
      marginBottom: '6px',
      borderBottom: '1px solid #bdc3c7',
      paddingBottom: '2px',
      fontWeight: 'bold',
      '@media (max-width: 768px)': {
        fontSize: '9px'
      }
    },
    fuecField: {
      display: 'flex',
      flexWrap: 'wrap',
      marginBottom: '4px',
      alignItems: 'center',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        alignItems: 'flex-start'
      }
    },
    fieldLabel: {
      fontWeight: 'bold',
      marginRight: '5px',
      minWidth: '120px',
      fontSize: '9px',
      '@media (max-width: 768px)': {
        minWidth: 'auto',
        marginBottom: '2px',
        fontSize: '8px'
      }
    },
    fieldValue: {
      flex: 1,
      fontSize: '9px',
      minWidth: '150px',
      '@media (max-width: 768px)': {
        minWidth: 'auto',
        fontSize: '8px',
        width: '100%'
      }
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '6px',
      fontSize: '8px',
      '@media (max-width: 768px)': {
        fontSize: '7px'
      }
    },
    tableHeader: {
      backgroundColor: '#ecf0f1',
      fontWeight: 'bold'
    },
    tableCell: {
      border: '1px solid #bdc3c7',
      padding: '4px',
      textAlign: 'left',
      fontSize: '8px',
      wordWrap: 'break-word',
      '@media (max-width: 768px)': {
        padding: '3px',
        fontSize: '7px'
      }
    },
    fuecFooter: {
      textAlign: 'center',
      marginTop: '15px',
      paddingTop: '8px',
      borderTop: '2px solid #2c3e50',
      '@media (max-width: 768px)': {
        marginTop: '10px'
      }
    },
    footerText: {
      fontWeight: 'bold',
      marginBottom: '6px',
      fontSize: '9px',
      '@media (max-width: 768px)': {
        fontSize: '8px'
      }
    },
    footerDate: {
      fontStyle: 'italic',
      fontSize: '8px',
      '@media (max-width: 768px)': {
        fontSize: '7px'
      }
    },
    twoColumns: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        gap: '5px'
      }
    },
    column: {
      flex: 1,
      minWidth: '200px',
      '@media (max-width: 768px)': {
        minWidth: 'auto'
      }
    },
    smallText: {
      fontSize: '8px',
      '@media (max-width: 768px)': {
        fontSize: '7px'
      }
    },
    companyInfo: {
      textAlign: 'center',
      fontSize: '8px',
      marginTop: '5px',
      lineHeight: '1.1',
      '@media (max-width: 768px)': {
        fontSize: '7px'
      }
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      fontSize: '16px'
    },
    error: {
      textAlign: 'center',
      padding: '20px',
      color: '#e74c3c',
      fontSize: '16px'
    },
    printButtonContainer: {
      textAlign: 'center',
      margin: '20px 0',
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      '@media (max-width: 768px)': {
        margin: '15px 0',
        padding: '15px'
      }
    },
    largePrintButton: {
      backgroundColor: '#3498db',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
      margin: '10px',
      minWidth: '250px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      '@media (max-width: 768px)': {
        padding: '15px 20px',
        fontSize: '16px',
        minWidth: '100%',
        margin: '10px 0'
      }
    },
    secondaryButton: {
      backgroundColor: '#2ecc71',
    },
    loadingButton: {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed',
      opacity: 0.7
    },
    buttonGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '10px',
      marginTop: '15px',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        gap: '10px'
      }
    },
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      color: 'white',
      fontSize: '18px'
    },
    spinner: {
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #3498db',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      animation: 'spin 2s linear infinite',
      marginBottom: '20px'
    },
    infoText: {
      fontSize: '14px',
      color: '#666',
      textAlign: 'center',
      marginTop: '10px',
      maxWidth: '600px',
      lineHeight: '1.5'
    }
  };

  // Mostrar loading
  if (loading) {
    return (
      <div style={styles.app}>
        <div style={styles.loading}>
          <h3>Cargando FUEC #{id}...</h3>
          <p>Consultando información del formato único...</p>
        </div>
      </div>
    );
  }

  // Mostrar error
  if (error) {
    return (
      <div style={styles.app}>
        <div style={styles.error}>
          <h3>Error al cargar FUEC</h3>
          <p>{error}</p>
          <br />
          <button 
            style={styles.largePrintButton}
            onClick={() => id && consultarFuec(id)}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.app}>
      {/* Overlay de carga para PDF */}
      {downloadingPDF && (
        <div style={styles.loadingOverlay}>
          <div style={styles.spinner}></div>
          <h3>Generando PDF...</h3>
          <p>Por favor espere un momento</p>
        </div>
      )}

      <div style={styles.modalHeader}>
        <h2 style={styles.modalTitle}>Formato FUEC #{fuecData.numero}</h2>
      </div>

      <div style={styles.printButtonContainer}>
        <h3 style={{ textAlign: 'center', marginBottom: '15px' }}>Opciones de Descarga</h3>
        
        <div style={styles.buttonGroup}>
          <button 
            style={{
              ...styles.largePrintButton,
              ...(downloadingPDF ? styles.loadingButton : {})
            }} 
            onClick={handleDownloadPDF}
            disabled={downloadingPDF || !fuecData.id}
          >
            {downloadingPDF ? (
              <>⏳ Generando PDF...</>
            ) : (
              <>📄 Descargar PDF (Recomendado)</>
            )}
          </button>
          
          {/* <button 
            style={{
              ...styles.largePrintButton,
              ...styles.secondaryButton
            }} 
            onClick={handlePrint}
          >
            🖨️ Imprimir Directamente
          </button> */}
        </div>
        
        <p style={styles.infoText}>
          <strong>Recomendación:</strong> Usa "Descargar PDF" para obtener un archivo de alta calidad que puedes guardar, compartir o imprimir posteriormente.
        </p>
        <p style={{ ...styles.infoText, fontSize: '12px', marginTop: '5px' }}>
          <em>Documento FUEC #{fuecData.numero} - Cliente: {datosCliente.nombre}</em>
        </p>
      </div>
      
      <div id="fuec-print-content" style={styles.fuecContainer}>
        <div style={styles.fuecHeader}>
          <h1 style={styles.fuecTitle}>FICHA DEL FORMATO DEL EXTRACTO DEL CONTRATO "FUEC"</h1>
          <img src={require("../../assets/images/logo_fuec.png")} alt="" style={{ width: '100%', maxWidth: '700px', height: 'auto', textAlign: "center" }} />
          <h2 style={styles.fuecSubtitle}>FORMATO ÚNICO DEL EXTRACTO DEL CONTRATO DEL SERVICIO PÚBLICO DE TRANSPORTE TERRESTRE AUTOMOTOR ESPECIAL</h2>
        </div>

        <div style={styles.fuecSection}>
          <div style={styles.fuecField}>
            <span style={styles.fieldLabel}>FUEC No.:</span>
            <span style={styles.fieldValue}>{fuecData.numero}</span>
          </div>
        </div>

        <div style={styles.twoColumns}>
          <div style={styles.column}>
            <div style={styles.fuecField}>
              <span style={styles.fieldLabel}>EMPRESA:</span>
              <span style={styles.fieldValue}>TRANSPORTES ESPECIALES DEL ORIENTE S.A.S</span>
            </div>
            <div style={styles.fuecField}>
              <span style={styles.fieldLabel}>NIT:</span>
              <span style={styles.fieldValue}>900645942-6</span>
            </div>
          </div>
          <div style={styles.column}>
            <div style={styles.fuecField}>
              <span style={styles.fieldLabel}>CONTRATO No.:</span>
              <span style={styles.fieldValue}>{datosContrato.numero}</span>
            </div>
          </div>
        </div>

        <div style={styles.fuecSection}>
          <div style={styles.fuecField}>
            <span style={styles.fieldLabel}>CONTRATANTE:</span>
            <span style={styles.fieldValue}>{datosCliente.nombre}</span>
          </div>
          <div style={styles.fuecField}>
            <span style={styles.fieldLabel}>NIT/CC:</span>
            <span style={styles.fieldValue}>{datosCliente.cc}</span>
          </div>
        </div>

        <div style={styles.fuecSection}>
          <div style={styles.fuecField}>
            <span style={styles.fieldLabel}>OBJETO:</span>
            <span style={{...styles.fieldValue, ...styles.smallText}}>{datosContrato.descripcion} {fuecData.objeto_extra}</span>
          </div>
        </div>

        <div style={styles.twoColumns}>
          <div style={styles.column}>
            <div style={styles.fuecField}>
              <span style={styles.fieldLabel}>ORIGEN:</span>
              <span style={{...styles.fieldValue, ...styles.smallText}}>{datosRecorrido.origen}</span>
            </div>
          </div>
          <div style={styles.column}>
            <div style={styles.fuecField}>
              <span style={styles.fieldLabel}>DESTINO:</span>
              <span style={{...styles.fieldValue, ...styles.smallText}}>{datosRecorrido.destino}</span>
            </div>
          </div>
        </div>
         <div style={styles.twoColumns}>
          <div style={styles.column}>
            <div style={styles.fuecField}>
              <span style={styles.fieldLabel}>CONVENIO:</span>
              <span style={{...styles.fieldValue, ...styles.smallText}}>{fuecData.convenio}</span>
            </div>
          </div>
          <div style={styles.column}>
            <div style={styles.fuecField}>
              <span style={styles.fieldLabel}>UNION TEMPORAL:</span>
              <span style={{...styles.fieldValue, ...styles.smallText}}>{fuecData.union_temporal}</span>
            </div>
          </div>
        </div>
{/* 
        <div style={styles.fuecSection}>
          <div style={styles.fuecField}>
            <span style={styles.fieldLabel}>CONVENIO:</span>
            <span style={styles.fieldValue}>{datosVehiculo.empresa_afiliada}</span>
          </div>
        </div> */}

        <div style={styles.fuecSection}>
          <h3 style={styles.sectionTitle}>VIGENCIA DEL CONTRATO</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{...styles.tableCell, ...styles.tableHeader}}></th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>DÍA</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>MES</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>AÑO</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}>FECHA DE INICIO</td>
                <td style={styles.tableCell}>{fechaInicioParts.day}</td>
                <td style={styles.tableCell}>{fechaInicioParts.month}</td>
                <td style={styles.tableCell}>{fechaInicioParts.year}</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>FECHA DE VENCIMIENTO</td>
                <td style={styles.tableCell}>{fechaFinParts.day}</td>
                <td style={styles.tableCell}>{fechaFinParts.month}</td>
                <td style={styles.tableCell}>{fechaFinParts.year}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={styles.fuecSection}>
          <h3 style={styles.sectionTitle}>CARACTERÍSTICAS DEL VEHÍCULO</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>PLACA</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>MODELO</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>MARCA</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>CLASE</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>NUMERO INTERNO</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>TARJETA OPERACIÓN</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}>{datosVehiculo.placa}</td>
                <td style={styles.tableCell}>{datosVehiculo.modelo}</td>
                <td style={styles.tableCell}>{datosVehiculo.clase}</td>
                <td style={styles.tableCell}>{datosVehiculo.marca}</td>  
                <td style={styles.tableCell}>{datosVehiculo.numero_interno}</td>
                <td style={styles.tableCell}>{fuecData.tarjeta_operacion}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={styles.fuecSection}>
          <h3 style={styles.sectionTitle}>DATOS DEL CONDUCTOR</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>CONDUCTOR</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>NOMBRES Y APELLIDOS</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>No CÉDULA</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>No LICENCIA</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>VIGENCIA</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}>1</td>
                <td style={styles.tableCell}>{datosConductor1.nombre}</td>
                <td style={styles.tableCell}>{datosConductor1.cedula}</td>
                <td style={styles.tableCell}>{datosConductor1.licencia}</td>
                <td style={styles.tableCell}>{datosConductor1.vigencia}</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>2</td>
                 <td style={styles.tableCell}>{datosConductor2.nombre}</td>
                <td style={styles.tableCell}>{datosConductor2.cedula}</td>
                <td style={styles.tableCell}>{datosConductor2.licencia}</td>
                <td style={styles.tableCell}>{datosConductor2.vigencia}</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>3</td>
                 <td style={styles.tableCell}>{datosConductor3.nombre}</td>
                <td style={styles.tableCell}>{datosConductor3.cedula}</td>
                <td style={styles.tableCell}>{datosConductor3.licencia}</td>
                <td style={styles.tableCell}>{datosConductor3.vigencia}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={styles.fuecSection}>
          <h3 style={styles.sectionTitle}>RESPONSABLE DEL CONTRATANTE</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>NOMBRES Y APELLIDOS</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>No CÉDULA</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>TELÉFONO</th>
                <th style={{...styles.tableCell, ...styles.tableHeader}}>DIRECCIÓN</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}>{datosContrato.nombre_responsable}</td>
                <td style={styles.tableCell}>{datosContrato.cedula_responsable}</td>
                <td style={styles.tableCell}>{datosContrato.telefono_responsable}</td>
                <td style={styles.tableCell}>{datosContrato.direccion_responsable}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={styles.fuecFooter}>
          <table style={styles.table}>
            <tbody>
              <tr>
                <td style={styles.tableCell}>
                  <p style={styles.footerText}>FORMATO ÚNICO DEL EXTRACTO DEL CONTRATO SEGÚN DECRETO 0348 DE 2015 Y RESOLUCIÓN 0006652 DE 2019</p>
                  <div style={styles.footerDate}>
                    FECHA DE ELAB: {formatDate(fuecData.fecha_registro)}
                  </div>
                </td>
                <td style={{...styles.tableCell, textAlign: 'center', width: '40%'}}>
                  <img src={require("../../assets/images/firma.png")} alt="" style={{ width: '100%', maxWidth: '200px', height: 'auto' }} />
                </td>
              </tr>
            </tbody>
          </table>
          
          <div style={styles.companyInfo}>
            TRANSPORTES ESPECIALES DEL ORIENTE S.A.S<br />
            CALLE 64A # 21 - 50 EDIF. PORTAL DEL CABLE<br />
            TEL. 3045049860 - 3046690903<br />
            MANIZALES - CALDAS
          </div>
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            {qrCodeUrl && (
              <>
                <img 
                  src={qrCodeUrl} 
                  alt={`Código QR FUEC ${fuecData.numero}`}
                  style={{
                    width: '100px',
                    height: '100px',
                    border: '1px solid #ccc',
                    padding: '5px',
                    backgroundColor: 'white',
                    margin: '0 auto'
                  }}
                  onError={(e) => {
                    console.error('❌ Error cargando QR:', e);
                    const backupQR = `https://chart.googleapis.com/chart?chs=150x150&cht=qr&chl=https://control.transorientesas.com/pages/comingsoon/Consultar_qr/${fuecData.id}`;
                    e.target.src = backupQR;
                  }}
                />
                <div style={{ fontSize: '8px', marginTop: '5px', color: '#666' }}>
                  Escanee para verificar autenticidad del documento
                </div>
              </>
            )}
          </div>
        </div>
        <div style={styles.fuecFooter}></div>
        <img src={require("../../assets/images/parte_trasera.png")} alt="" style={{ width: '100%', height: 'auto' }} />
      </div>

      {/* Añadir animación CSS para el spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default FuecModal;